/**
 * VistaDemo — live Vista Equity portfolio demo dashboard.
 *
 * Everything on this page is fetched from the live backend:
 *   GET  /api/v1/demo/vista/portfolio-overview   (Scene 1 cards grid)
 *   POST /api/v1/demo/vista/simulate-savings     (Scene 2 real-QUBO numbers)
 *   POST /api/v1/demo/vista/project-full-portfolio (Scene 3 scale-to-90)
 *   GET  /api/v1/demo/vista/zero-knowledge-proof (Scene 5)
 *   GET  /api/v1/demo/vista/live-tape            (SSE stream — Live)
 *
 * Auth: `Authorization: Bearer <admin-scoped proxy key>` — captured via the
 *       small login overlay at the top so the same page can be opened on
 *       stage with a one-time key paste.
 *
 * Aesthetic: amber/gold on charcoal, asymmetric grid, large numerical
 * counters that animate from 0 → real value (via Framer Motion).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import VistaAutopilot from "@/pages/VistaAutopilot";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KEY_STORAGE = "qnneal_vista_demo_key";

// ─────────────────────────────────────────────────────────────────
// Compact USD formatter — auto-flips between $X.YM and $XYZk
// ─────────────────────────────────────────────────────────────────
function fmtUsdShort(n) {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `$${Math.round(n / 1_000)}k`;
  return `$${n.toFixed(0)}`;
}

// ─────────────────────────────────────────────────────────────────
// Animated number counter (0 → target with easing)
// ─────────────────────────────────────────────────────────────────
function AnimatedNumber({ value, prefix = "", suffix = "", decimals = 0, className = "" }) {
  const motionVal = useMotionValue(0);
  const spring = useSpring(motionVal, { stiffness: 60, damping: 18, mass: 1 });
  const display = useTransform(spring, (v) => {
    if (decimals > 0) return v.toFixed(decimals);
    return Math.round(v).toLocaleString();
  });
  useEffect(() => {
    motionVal.set(value || 0);
  }, [value, motionVal]);
  return (
    <span className={className}>
      {prefix}<motion.span>{display}</motion.span>{suffix}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────
// Auth overlay (key capture)
// ─────────────────────────────────────────────────────────────────
function AuthGate({ onKey }) {
  const [k, setK] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm">
      <Card className="w-full max-w-md border-amber-500/40 bg-zinc-950 text-zinc-100">
        <CardHeader>
          <CardTitle className="text-amber-400">QNNEAL Vista Demo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-zinc-400">
            Paste an admin-scoped proxy key (<code>qn_live_…</code>). Stays in
            memory only — never sent anywhere except the QNNEAL API.
          </p>
          <Input
            data-testid="vista-key-input"
            type="password"
            value={k}
            onChange={(e) => setK(e.target.value)}
            placeholder="qn_live_..."
            className="border-amber-500/30 bg-zinc-900 text-zinc-100"
          />
          <Button
            data-testid="vista-key-submit"
            onClick={() => {
              if (!k.startsWith("qn_live_") && !k.startsWith("qn_test_")) {
                toast.error("Key must start with qn_live_ or qn_test_");
                return;
              }
              sessionStorage.setItem(KEY_STORAGE, k);
              localStorage.setItem(KEY_STORAGE, k); // also stage mode
              onKey(k);
            }}
            className="w-full bg-amber-500 text-black hover:bg-amber-400"
          >
            Unlock Demo
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
export default function VistaDemo() {
  // Mode detection (priority: autopilot > stage > normal)
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const autopilotMode = params.get("autopilot") === "true";
  const stageMode = params.get("stage") === "true";
  const storage = (autopilotMode || stageMode) ? localStorage : sessionStorage;

  const [apiKey, setApiKey] = useState(() => storage.getItem(KEY_STORAGE) || "");
  const [overview, setOverview] = useState(null);
  const [sim, setSim] = useState(null);
  const [proj, setProj] = useState(null);
  const [zk, setZk] = useState(null);
  const [loadingScene, setLoadingScene] = useState(null);
  const [tape, setTape] = useState({ events: [], total: 0, running: false, count: 0 });
  const [shortcutFlash, setShortcutFlash] = useState(null);
  const tapeAbortRef = useRef(null);
  const autoSimFiredRef = useRef(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" }), [apiKey]);

  // Auto-load Scene 1 once the key is entered
  useEffect(() => {
    if (!apiKey) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/v1/demo/vista/portfolio-overview`, { headers });
        if (r.status === 401 || r.status === 403) {
          toast.error("Key rejected — try again");
          storage.removeItem(KEY_STORAGE);
          setApiKey("");
          return;
        }
        const d = await r.json();
        setOverview(d);
      } catch (e) {
        toast.error(`Network error: ${e.message}`);
      }
    })();
  }, [apiKey, headers, storage]);

  const runScene2 = async () => {
    setLoadingScene("scene2");
    try {
      const r = await fetch(`${BACKEND}/api/v1/demo/vista/simulate-savings`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          companies_to_simulate: 30,
          simulation_period_days: 30,
          show_per_company_breakdown: true,
          sample_calls_per_company: 250,
        }),
      });
      const d = await r.json();
      setSim(d);
    } catch (e) {
      toast.error(`Scene 2 failed: ${e.message}`);
    } finally {
      setLoadingScene(null);
    }
  };

  const runScene3 = async () => {
    setLoadingScene("scene3");
    try {
      const r = await fetch(`${BACKEND}/api/v1/demo/vista/project-full-portfolio`, {
        method: "POST",
        headers,
      });
      setProj(await r.json());
    } catch (e) {
      toast.error(`Scene 3 failed: ${e.message}`);
    } finally {
      setLoadingScene(null);
    }
  };

  const runScene5 = async () => {
    setLoadingScene("scene5");
    try {
      const r = await fetch(`${BACKEND}/api/v1/demo/vista/zero-knowledge-proof`, { headers });
      setZk(await r.json());
    } catch (e) {
      toast.error(`Scene 5 failed: ${e.message}`);
    } finally {
      setLoadingScene(null);
    }
  };

  const startLiveTape = async () => {
    if (tape.running) {
      tapeAbortRef.current?.abort();
      return;
    }
    setTape({ events: [], total: 0, running: true, count: 0 });
    const ctrl = new AbortController();
    tapeAbortRef.current = ctrl;
    try {
      const r = await fetch(`${BACKEND}/api/v1/demo/vista/live-tape?duration_s=30&interval_ms=250`, {
        headers,
        signal: ctrl.signal,
      });
      if (!r.ok) {
        toast.error(`Live tape rejected: HTTP ${r.status}`);
        setTape((t) => ({ ...t, running: false }));
        return;
      }
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Parse SSE frames (separated by blank lines)
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const lines = frame.split("\n");
          let event = "message";
          let data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) event = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trim();
          }
          if (!data) continue;
          let payload;
          try { payload = JSON.parse(data); } catch { continue; }
          if (event === "decision") {
            setTape((t) => ({
              ...t,
              events: [payload, ...t.events].slice(0, 25),
              total: payload.running_total_saved_usd,
              count: payload.n,
            }));
          } else if (event === "completed") {
            setTape((t) => ({ ...t, running: false, total: payload.total_saved_usd }));
            toast.success(
              `Tape complete: ${payload.decisions} decisions · $${payload.total_saved_usd.toFixed(2)} saved in ${payload.elapsed_s.toFixed(1)}s`
            );
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error(`Tape error: ${e.message}`);
    } finally {
      setTape((t) => ({ ...t, running: false }));
      tapeAbortRef.current = null;
    }
  };

  // ── Tamper demo (Scene 4) — admin endpoint ──
  const runTamperDemo = async () => {
    setLoadingScene("scene4");
    try {
      const r = await fetch(`${BACKEND}/api/v1/audit/_tamper_demo`, { method: "POST", headers });
      if (r.status === 404) {
        toast.error("Tamper demo disabled (production mode)");
        return;
      }
      const d = await r.json();
      const v = d.verification_result || {};
      if (v.verified === false) {
        toast.success(
          `🔓 Chain BROKEN — ${v.broken_links?.length || 1} entries failed verification. Auto-revert in ${d.auto_revert_in_seconds}s.`,
          { duration: 8000 }
        );
      } else {
        toast.message("Tamper applied — refresh /audit/verify to see broken chain");
      }
    } catch (e) {
      toast.error(`Tamper demo error: ${e.message}`);
    } finally {
      setLoadingScene(null);
    }
  };

  // ── Reset all derived state (keeps auth) ──
  const resetAll = () => {
    if (tapeAbortRef.current) tapeAbortRef.current.abort();
    setSim(null);
    setProj(null);
    setZk(null);
    setTape({ events: [], total: 0, running: false, count: 0 });
    toast.message("Demo state reset · Scene 1 reloading");
    (async () => {
      const r = await fetch(`${BACKEND}/api/v1/demo/vista/portfolio-overview`, { headers });
      if (r.ok) setOverview(await r.json());
    })();
  };

  // ── Stage-mode auto-run: fire Scene 2 on first overview load ──
  useEffect(() => {
    if (!apiKey || !stageMode || !overview || autoSimFiredRef.current) return;
    autoSimFiredRef.current = true;
    runScene2();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageMode, overview, apiKey]);

  // ── Keyboard shortcuts: Shift+L / Shift+T / Shift+R / Shift+S/P/Z ──
  useEffect(() => {
    if (!apiKey) return;
    const flash = (label) => {
      setShortcutFlash(label);
      setTimeout(() => setShortcutFlash(null), 800);
    };
    const handler = (e) => {
      const tag = (e.target.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key.toUpperCase();
      const map = {
        L: ["⌨ Shift+L · Live Tape", startLiveTape],
        T: ["⌨ Shift+T · Tamper Demo", runTamperDemo],
        R: ["⌨ Shift+R · Reset", resetAll],
        S: ["⌨ Shift+S · Scene 2", runScene2],
        P: ["⌨ Shift+P · Project to 90", runScene3],
        Z: ["⌨ Shift+Z · Zero-Knowledge", runScene5],
      };
      if (!map[key]) return;
      e.preventDefault();
      const [label, fn] = map[key];
      flash(label);
      fn();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, tape.running]);

  if (!apiKey) return <AuthGate onKey={setApiKey} />;

  // ── Autopilot mode: hand off to the cinematic player ──
  if (autopilotMode) return <VistaAutopilot apiKey={apiKey} />;

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100 selection:bg-amber-500/40">
      {/* Shortcut flash (top-right ephemeral indicator) */}
      {shortcutFlash && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="fixed top-6 right-6 z-50 rounded-md border border-amber-500/40 bg-zinc-950/90 px-4 py-2 text-sm text-amber-400 backdrop-blur"
          data-testid="shortcut-flash"
        >
          {shortcutFlash}
        </motion.div>
      )}

      {/* Stage-mode shortcut legend */}
      {stageMode && (
        <div className="fixed bottom-6 left-6 z-40 rounded-md border border-zinc-800 bg-zinc-950/80 px-4 py-3 text-xs text-zinc-500 backdrop-blur space-y-1 font-mono" data-testid="stage-shortcuts">
          <div><kbd className="text-amber-400">Shift+S</kbd> Scene 2 · QUBO sim</div>
          <div><kbd className="text-amber-400">Shift+P</kbd> Project to 90</div>
          <div><kbd className="text-amber-400">Shift+L</kbd> Live tape</div>
          <div><kbd className="text-amber-400">Shift+Z</kbd> Zero-knowledge</div>
          <div><kbd className="text-amber-400">Shift+T</kbd> Tamper demo</div>
          <div><kbd className="text-amber-400">Shift+R</kbd> Reset</div>
        </div>
      )}
      {/* Header */}
      <header className="border-b border-amber-500/20 px-10 py-6">
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-amber-400" data-testid="vista-demo-title">
              QNNEAL × Vista Equity
            </h1>
            <p className="mt-1 text-sm uppercase tracking-[0.3em] text-zinc-500">
              Quantum-routed portfolio inference savings · live demo
            </p>
          </div>
          <Badge variant="outline" className="border-amber-500/40 text-amber-400">
            zero-knowledge · audit-chained · 27-model QUBO
          </Badge>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-10 py-12 space-y-16">
        {/* Hero — Total annual savings */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-center" data-testid="vista-hero">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Live computed annual savings</p>
            <div className="text-7xl font-extralight tracking-tight text-amber-400 tabular-nums" data-testid="vista-hero-savings">
              <AnimatedNumber value={sim?.results?.annual_savings || 0} prefix="$" />
            </div>
            <p className="mt-4 max-w-2xl text-zinc-400 leading-relaxed">
              Across {overview?.portfolio?.ai_active_companies || 30} AI-active portfolio companies — driven by{" "}
              <strong className="text-zinc-100">
                {sim?.simulation?.qubo_solves_executed?.toLocaleString() || 0}
              </strong>{" "}
              real Layer-1 QUBO solves on representative traffic. Quality target met on{" "}
              <strong className="text-amber-400">
                {sim?.results?.quality_target_met_pct ?? "—"}%
              </strong>{" "}
              of decisions.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="run-scene-2-btn"
                disabled={loadingScene === "scene2"}
                onClick={runScene2}
                className="bg-amber-500 text-black hover:bg-amber-400 px-6 py-6 text-base"
              >
                {loadingScene === "scene2" ? "Running real QUBO…" : "Run Scene 2 — Real QUBO Simulation"}
              </Button>
              <Button
                data-testid="run-scene-3-btn"
                disabled={loadingScene === "scene3"}
                onClick={runScene3}
                variant="outline"
                className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 px-6 py-6 text-base"
              >
                {loadingScene === "scene3" ? "Projecting…" : "Scale to all 90 →"}
              </Button>
              <Button
                data-testid="run-live-tape-btn"
                onClick={startLiveTape}
                variant="outline"
                className={`px-6 py-6 text-base ${tape.running ? "border-red-500/60 text-red-400" : "border-amber-500/40 text-amber-400 hover:bg-amber-500/10"}`}
              >
                {tape.running ? `■ Stop tape (${tape.count})` : "▶ 30-Second Live Tape"}
              </Button>
            </div>
          </div>

          {/* Stats column */}
          <div className="space-y-4" data-testid="vista-hero-stats">
            <Stat
              label="Monthly portfolio spend"
              value={overview?.portfolio?.total_monthly_ai_spend || 0}
              prefix="$"
              testId="stat-monthly-spend"
            />
            <Stat
              label="Monthly savings (Optimized)"
              value={sim?.results?.monthly_savings || 0}
              prefix="$"
              accent
              testId="stat-monthly-savings"
            />
            <Stat
              label="Savings %"
              value={sim?.results?.savings_percentage || 0}
              suffix="%"
              decimals={1}
              accent
              testId="stat-savings-pct"
            />
            <Stat
              label="Quantum speedup at 5k req/s"
              value={
                sim?.quantum_advantage?.classical_solve_time_ms && sim?.quantum_advantage?.quantum_solve_time_ms
                  ? Math.round(
                      sim.quantum_advantage.classical_solve_time_ms /
                        sim.quantum_advantage.quantum_solve_time_ms
                    )
                  : 0
              }
              suffix="×"
              testId="stat-quantum-speedup"
            />
          </div>
        </section>

        {/* Live tape */}
        {(tape.running || tape.events.length > 0) && (
          <section data-testid="live-tape-section" className="border border-amber-500/30 rounded-lg bg-zinc-950/60 p-8">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-500">Live Routing Tape</p>
                <h2 className="text-2xl font-light text-amber-400">
                  Saved so far: $<AnimatedNumber value={tape.total} decimals={2} />
                </h2>
              </div>
              <Badge className={tape.running ? "bg-red-500/20 text-red-400 border-red-500/40" : "bg-zinc-800 text-zinc-400"}>
                {tape.running ? "● LIVE" : "stopped"}
              </Badge>
            </div>
            <div className="space-y-1 font-mono text-sm">
              {tape.events.map((e) => (
                <motion.div
                  key={e.n}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="grid grid-cols-12 gap-3 border-b border-zinc-900 py-2"
                >
                  <span className="col-span-1 text-zinc-600 tabular-nums">#{e.n}</span>
                  <span className="col-span-3 truncate text-zinc-400">{e.company.split(" (")[0]}</span>
                  <span className="col-span-1 text-zinc-500 text-xs uppercase">{e.task_type}</span>
                  <span className="col-span-3 truncate text-zinc-300">
                    {e.from_model} <span className="text-amber-500">→</span>{" "}
                    <strong className={e.kept_baseline ? "text-zinc-500" : "text-amber-400"}>
                      {e.to_model}
                    </strong>
                  </span>
                  <span className="col-span-2 tabular-nums text-emerald-400 text-right">
                    +${e.saved_usd.toFixed(6)}
                  </span>
                  <span className="col-span-2 tabular-nums text-amber-300 text-right font-bold">
                    ${e.running_total_saved_usd.toFixed(4)}
                  </span>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Portfolio grid */}
        {overview?.companies && (
          <section data-testid="portfolio-grid">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-500">Scene 1 · The 30 AI-active companies</p>
                <h2 className="text-2xl font-light text-zinc-100">Portfolio AI spend</h2>
              </div>
              <p className="text-sm text-zinc-500">Power-law distribution · Top 5 ≈ 40% of spend</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {overview.companies.map((c) => {
                const simRow = sim?.per_company?.find((p) => p.name === c.name);
                const savingsPct = simRow?.savings_pct ?? null;
                const colorClass =
                  savingsPct === null ? "border-zinc-800"
                    : savingsPct >= 50 ? "border-emerald-500/40 bg-emerald-500/5"
                    : savingsPct >= 35 ? "border-amber-500/40 bg-amber-500/5"
                    : "border-zinc-700/40 bg-zinc-800/20";
                return (
                  <motion.div
                    key={c.id}
                    layout
                    whileHover={{ y: -2 }}
                    className={`rounded-md border ${colorClass} p-4 transition-colors`}
                  >
                    <div className="flex items-baseline justify-between">
                      <p className="text-sm font-medium text-zinc-200 truncate" title={c.name}>
                        {c.name.split("(")[1]?.replace(")", "") || c.name}
                      </p>
                      {savingsPct !== null && (
                        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 ml-2 shrink-0 tabular-nums">
                          −{savingsPct.toFixed(1)}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{c.vertical}</p>
                    <div className="flex items-baseline justify-between mt-3">
                      <p className="text-lg font-light tabular-nums text-zinc-100">{fmtUsdShort(c.monthly_ai_spend)}/mo</p>
                      <p className="text-xs text-zinc-500">{c.primary_model}</p>
                    </div>
                    {simRow && (
                      <p className="mt-2 text-xs text-emerald-400 tabular-nums">
                        → {fmtUsdShort(simRow.optimized_monthly)} optimized
                      </p>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* Scale to 90 */}
        {proj && (
          <section data-testid="scale-projection">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-amber-500/40 bg-zinc-950 text-zinc-100">
                <CardHeader className="pb-2"><CardTitle className="text-amber-400 text-base">Year 1 · 30 cos</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-3xl font-light tabular-nums">
                    <AnimatedNumber value={proj.projection.current_30_companies.annual_savings} prefix="$" />
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">{proj.projection.current_30_companies.savings_rate_pct}% measured savings rate</p>
                </CardContent>
              </Card>
              <Card className="border-amber-500/40 bg-zinc-950 text-zinc-100">
                <CardHeader className="pb-2"><CardTitle className="text-amber-400 text-base">Full 90 cos · annual</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-3xl font-light tabular-nums">
                    <AnimatedNumber value={proj.projection.full_90_companies.estimated_annual_savings} prefix="$" />
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">conservative — same AI intensity</p>
                </CardContent>
              </Card>
              <Card className="border-amber-500/40 bg-zinc-950 text-zinc-100">
                <CardHeader className="pb-2"><CardTitle className="text-amber-400 text-base">3-Year cumulative</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-3xl font-light tabular-nums">
                    <AnimatedNumber value={proj.projection.with_growth.three_year_total} prefix="$" />
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">QNNEAL revenue (20% take): ${proj.qnneal_revenue_model.three_year_total.toLocaleString()}</p>
                </CardContent>
              </Card>
            </div>
            <p className="text-xs text-zinc-500 mt-4 italic">
              At {proj.quantum_advantage_at_scale.concurrent_requests_at_90_companies.toLocaleString()} concurrent
              requests, classical optimization extrapolates to {(proj.quantum_advantage_at_scale.classical_solve_time_ms/1000).toFixed(0)}s
              — operationally unusable. Quantum hybrid: {(proj.quantum_advantage_at_scale.quantum_solve_time_ms/1000).toFixed(1)}s.
            </p>
          </section>
        )}

        {/* Zero-Knowledge */}
        <section data-testid="zk-section">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-2xl font-light text-zinc-100">We never see your data</h2>
            <Button
              data-testid="run-scene-5-btn"
              variant="outline"
              size="sm"
              onClick={runScene5}
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            >
              {loadingScene === "scene5" ? "Running…" : "Run a live ZK proof"}
            </Button>
          </div>
          {zk && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-emerald-500/40 bg-emerald-500/5 text-zinc-100">
                <CardHeader className="pb-2"><CardTitle className="text-emerald-400 text-sm">QNNEAL sees</CardTitle></CardHeader>
                <CardContent className="text-xs font-mono text-zinc-300 space-y-1">
                  {Object.entries(zk.what_qnneal_sees).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-zinc-900 pb-1">
                      <span className="text-zinc-500">{k}</span>
                      <span>{String(v)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card className="border-red-500/40 bg-red-500/5 text-zinc-100">
                <CardHeader className="pb-2"><CardTitle className="text-red-400 text-sm">QNNEAL never sees</CardTitle></CardHeader>
                <CardContent className="text-xs font-mono text-zinc-300 space-y-1">
                  {Object.entries(zk.what_qnneal_never_sees).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-zinc-900 pb-1">
                      <span className="text-zinc-500">{k}</span>
                      <span className="text-zinc-600">{String(v)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
          {zk?.routing_decision && (
            <p className="mt-3 text-sm text-zinc-400">
              Live solve: <span className="text-amber-400 font-mono">gpt-4o → {zk.routing_decision.selected_model}</span> ·
              saved <span className="text-emerald-400">${zk.routing_decision.savings_per_call_usd.toFixed(6)}</span>{" "}
              ({zk.routing_decision.savings_pct}%) on this single call · solver took {zk.routing_decision.solver_latency_ms.toFixed(2)}ms.
            </p>
          )}
        </section>

        {/* Methodology — credibility infrastructure, always visible */}
        {overview?.companies && (
          <section data-testid="methodology-section" className="border border-zinc-800 rounded-lg bg-zinc-950/40 p-6">
            <details>
              <summary className="cursor-pointer text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-amber-400 transition-colors">
                Methodology · how these numbers were derived (click to expand)
              </summary>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <p className="text-zinc-300 font-medium mb-2">Portfolio spend distribution</p>
                  <ul className="space-y-2 text-zinc-400 text-xs leading-relaxed">
                    <li>· <strong className="text-zinc-200">Floor:</strong> $100K/mo (the production-AI minimum — anything below isn't credibly running agentic AI at scale).</li>
                    <li>· <strong className="text-zinc-200">Ceiling:</strong> $950K/mo (single largest whale, calibrated against public top-AI-spend benchmarks).</li>
                    <li>· <strong className="text-zinc-200">Three tiers:</strong> 5 whales (~$610K avg), 15 mid-market (~$290K), 10 active adopters (~$130K).</li>
                    <li>· <strong className="text-zinc-200">Per-company spread:</strong> log-normal noise (right-skewed — matches real enterprise revenue distributions, not symmetric Gaussian).</li>
                    <li>· <strong className="text-zinc-200">Deterministic seed:</strong> {overview.deterministic_seed}. Re-run anytime → identical result. No cherry-picking.</li>
                  </ul>
                </div>
                <div>
                  <p className="text-zinc-300 font-medium mb-2">Savings derivation (no hardcoding)</p>
                  <ul className="space-y-2 text-zinc-400 text-xs leading-relaxed">
                    <li>· <strong className="text-zinc-200">Real Layer-1 QUBO</strong>: 7,500 actual quantum-formulated solves (250 per company × 30 companies). Solver code: <code className="text-amber-400">/app/backend/proxy/qubo_solver.py</code>.</li>
                    <li>· <strong className="text-zinc-200">Quality floors enforced per task type:</strong> simple ≥ 0.65, medium ≥ 0.78, complex ≥ 0.88, critical ≥ 0.95. The QUBO is mathematically prevented from selecting a model that fails to meet the task's required floor.</li>
                    <li>· <strong className="text-zinc-200">Cost-floor guard:</strong> if QUBO ever picks a model that costs MORE than baseline for a given call, baseline is kept. QNNEAL never raises customer cost.</li>
                    <li>· <strong className="text-zinc-200">Quality-target metric:</strong> {sim?.results?.quality_target_met_pct ?? "—"}% of all decisions met or exceeded the task's required quality floor.</li>
                    <li>· <strong className="text-zinc-200">Cryptographic audit:</strong> every routing decision hash-chained to <code className="text-amber-400">/api/v1/audit/verify</code>. Tamper-demo: <code className="text-amber-400">/api/v1/audit/_tamper_demo</code>.</li>
                  </ul>
                </div>
              </div>
              <p className="mt-6 pt-4 border-t border-zinc-900 text-xs text-zinc-500 italic leading-relaxed">
                Verifiable independently: the deterministic seed means any Vista analyst can pull this exact 30-company distribution
                and re-run our QUBO against it to reproduce the savings number to the dollar. No black-box claims.
              </p>
            </details>
          </section>
        )}

        {/* Footer */}
        <footer className="pt-8 border-t border-zinc-900 text-xs text-zinc-600">
          Audit chain: <code>/api/v1/audit/verify</code> ·
          Tamper demo: <code>/api/v1/audit/_tamper_demo</code> (env-gated) ·
          Catalog refresh status: <code>/api/v1/catalog/refresh-status</code>
        </footer>
      </main>
    </div>
  );
}

function Stat({ label, value, prefix = "", suffix = "", decimals = 0, accent, testId }) {
  return (
    <div className="border-l-2 border-amber-500/30 pl-4 py-1" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</p>
      <p className={`text-2xl font-light tabular-nums ${accent ? "text-amber-400" : "text-zinc-100"}`}>
        <AnimatedNumber value={value || 0} prefix={prefix} suffix={suffix} decimals={decimals} />
      </p>
    </div>
  );
}
